import { FC, useEffect } from 'react';
import { useSnapshot } from 'valtio';
import { useIsUserReadOnlyRole } from '../../members/api/user-roles-api';
import {
  addConnection,
  checkIsSelfConnection,
  configureConnection,
  removeConnection,
} from '../actions/configure-connection-actions';
import SelectedConnectionStore from '../stores/slected-connection-store';
import ConnectionsConfigureStore from '../stores/connection-configure-store';
import {
  Connection,
  ConnectionFormFields,
  ConnectionType,
} from '../types/connection-types';
import {
  resetForm,
  setInitialFormValues,
} from '../utils/connection-configure-store-utils';
import AzureADConnectionForm from './AzureADConnectionForm';
import ConnectionForm from './ConnectionForm';
import OktaConnectionForm from './OktaConnectionForm';
import ScanStore from '../../new-scan/store/scan-store';

const ConnectionFormSwitcher: FC<
  React.PropsWithChildren<ConnectionFormSwitcherProps>
> = ({ onClose, connection }) => {
  const { connectionType } = connection;

  const isReadOnlyRole = useIsUserReadOnlyRole();

  const {
    domain,
    clientId,
    clientSecret,
    useCommonEndpoint,
    isUpdatable,
    isLoadingSave,
    errorMessage,
  } = useSnapshot(ConnectionsConfigureStore, { sync: true });
  const { isDemoEnabled } = useSnapshot(ScanStore);

  const { isLoadingAdd, isLoadingRemove } = useSnapshot(
    SelectedConnectionStore,
  );

  useEffect(() => {
    setInitialFormValues(connection);

    return () => {
      resetForm();
    };
  }, [connection]);

  const isSelfConnection = checkIsSelfConnection(connection);

  const connectionFormFields: ConnectionFormFields = {
    domain,
    clientId,
    clientSecret,
    useCommonEndpoint,
    strategy: connection.strategy,
  };

  switch (connectionType) {
    case ConnectionType.Auth0UsernamePassword:
    case ConnectionType.GoogleOauth2:
    case ConnectionType.Github:
      return (
        <ConnectionForm
          connection={connection}
          onAddConnection={addConnection}
          onRemoveConnection={removeConnection}
          onClose={onClose}
          isReadOnlyRole={isReadOnlyRole || isDemoEnabled}
          isLoadingAdd={isLoadingAdd}
          isLoadingRemove={isLoadingRemove}
          isSelfConnection={isSelfConnection}
          errorMessage={errorMessage}
        />
      );
    case ConnectionType.AzureAD:
      return (
        <AzureADConnectionForm
          connection={connection}
          connectionFormFields={connectionFormFields}
          onSaveConnection={configureConnection}
          onRemoveConnection={removeConnection}
          isReadOnlyRole={isReadOnlyRole || isDemoEnabled}
          isUpdatable={isUpdatable}
          isLoadingSave={isLoadingSave}
          isLoadingRemove={isLoadingRemove}
          isSelfConnection={isSelfConnection}
          errorMessage={errorMessage}
        />
      );
    case ConnectionType.Okta:
      return (
        <OktaConnectionForm
          connection={connection}
          connectionFormFields={connectionFormFields}
          onSaveConnection={configureConnection}
          onRemoveConnection={removeConnection}
          isReadOnlyRole={isReadOnlyRole || isDemoEnabled}
          isUpdatable={isUpdatable}
          isLoadingSave={isLoadingSave}
          isLoadingRemove={isLoadingRemove}
          isSelfConnection={isSelfConnection}
          errorMessage={errorMessage}
        />
      );

    default:
      return null;
  }
};

export interface ConnectionFormSwitcherProps {
  connection: Connection;
  onClose: () => void;
}

export default ConnectionFormSwitcher;
